import { useState, useCallback, useMemo } from 'react';
import { Typography, Button, List, Tag, Row, Col, Space, Grid, message, Spin, Table, Radio, Tooltip, Card, Badge } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined, TableOutlined, EyeOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCalendarEvents, deleteCalendarEvent } from '@/api/calendar';
import { CalendarEvent, EventType } from '@/types/community/calendar';
import { useAuth } from '@/contexts/AuthContext';
import CalendarEventModal from '@/components/features/community/CalendarEventModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommonCalendar from '@/components/common/Calendar';

const { Title, Text } = Typography;
const { Button: RadioButton } = Radio;
const { useBreakpoint } = Grid;

const eventTypeColors: Record<EventType, string> = {
  meeting: 'blue',
  activity: 'green',
  other: 'purple'
};

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'activity', label: 'Activity' },
  { value: 'other', label: 'Other' }
];

type ViewMode = 'calendar' | 'table';

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export default function CommunityCalendar() {
  const { t } = useTranslation(['calendar', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['calendarEvents', firestoreUser?.workspace?.id],
    queryFn: () => getCalendarEvents({ 
      firestoreUser: firestoreUser!,
      pageSize: 100 // Get all events for this view
    }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const deleteEventMutation = useMutation({
    mutationFn: (id: string) => deleteCalendarEvent(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('calendar:eventDeleted'));
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      setIsDeleteModalVisible(false);
      setSelectedEvent(null);
      setDeleteError(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error);
    }
  });

  const eventsList = useMemo(() => eventsData?.events || [], [eventsData]);

  const handleSelectDate = useCallback((date: Dayjs) => {
    setSelectedDate(date);
  }, []);

  const handleAddEvent = useCallback(() => {
    setSelectedEvent(null);
    setIsModalOpen(true);
  }, []);

  const handleEditEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  }, []);

  const handleViewEvent = useCallback((event: CalendarEvent) => {
    navigate(`/community/calendar/${event.id}`, { 
      state: { 
        event,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsDeleteModalVisible(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedEvent) {
      deleteEventMutation.mutate(selectedEvent.id!);
    }
  }, [selectedEvent, deleteEventMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedEvent(null);
  }, []);

  const handleOrganizerClick = useCallback((organizerId: string, organizerType: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (organizerType === 'employee') {
      navigate(`/resources/employees/${organizerId}`);
    } else if (organizerType === 'resident') {
      navigate(`/resources/residents/${organizerId}`);
    } else if (organizerType === 'vendor') {
      navigate(`/resources/vendors/${organizerId}`);
    }
  }, [navigate]);

  const getEventsForSelectedDate = useCallback(() => {
    return eventsList
      .filter(event => dayjs(event.start).isSame(selectedDate, 'day'))
      .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());
  }, [eventsList, selectedDate]);

  const selectedDateEvents = getEventsForSelectedDate();

  const renderEventCard = useCallback((event: CalendarEvent) => (
    <Card
      key={event.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewEvent(event)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditEvent(event)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDeleteEvent(event)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={
          <Space>
            <Tag color={eventTypeColors[event.type]}>
              {t(`calendar:types.${event.type}`, event.type)}
            </Tag>
            {event.title}
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <div>
              <Text type="secondary">
                {dayjs(event.start).format('MMM D, YYYY h:mm A')} - {dayjs(event.end).format('h:mm A')}
              </Text>
            </div>
            {/* Organizer Info */}
            {event.hostedBy && (
              <div style={{ marginBottom: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Hosted by:{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                    onClick={(e) => handleOrganizerClick(event.hostedBy!.id, event.hostedBy!.type, e)}
                  >
                    {event.hostedBy.name}
                  </Button>
                </Text>
              </div>
            )}
            {event.description && (
              <div>
                <Text>{event.description}</Text>
              </div>
            )}
          </Space>
        }
      />
    </Card>
  ), [t, handleViewEvent, handleEditEvent, handleDeleteEvent, handleOrganizerClick]);

  const renderCalendarView = useCallback(() => (
    <Row gutter={16}>
      <Col xs={24} md={24}>
        <CommonCalendar
          events={eventsList}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          eventTypeColors={eventTypeColors}
          getEventDate={(event: CalendarEvent) => dayjs(event.start)}
          getEventTitle={(event: CalendarEvent) => event.title}
          getEventType={(event: CalendarEvent) => event.type}
          getEventColor={(event: CalendarEvent) => eventTypeColors[event.type]}
          onEventClick={(event: CalendarEvent) => handleViewEvent(event)}
          showEventCount={false}
          defaultView="month"
        />
      </Col>
      {screens.xs && (
        <Col xs={24}>
          <Title level={4} style={{ marginTop: 24 }}>
            {t('calendar:eventsFor', { date: selectedDate.format('MMMM D, YYYY') })}
          </Title>
          {selectedDateEvents.length > 0 ? (
            <List
              dataSource={selectedDateEvents}
              renderItem={renderEventCard}
              rowKey="id"
              grid={{ gutter: 16, xs: 1, sm: 1 }}
            />
          ) : (
            <Text>{t('calendar:noEvents')}</Text>
          )}
        </Col>
      )}
    </Row>
  ), [eventsList, selectedDate, handleSelectDate, eventTypeColors, handleViewEvent, selectedDateEvents, renderEventCard, screens.xs, t]);

  const renderTableView = useCallback(() => {
    const columns = [
      {
        title: t('calendar:title'),
        dataIndex: 'title',
        key: 'title',
        render: (text: string, record: CalendarEvent) => (
          <Button type="link" onClick={() => handleViewEvent(record)}>
            {text}
          </Button>
        ),
      },
      {
        title: t('calendar:type'),
        dataIndex: 'type',
        key: 'type',
        render: (type: EventType) => (
          <Tag color={eventTypeColors[type]}>
            {EVENT_TYPES.find(t => t.value === type)?.label}
          </Tag>
        ),
      },
      {
        title: t('calendar:startTime'),
        dataIndex: 'start',
        key: 'start',
        render: (date: Date) => dayjs(date).format('MMM D, YYYY h:mm A'),
        sorter: (a: CalendarEvent, b: CalendarEvent) => dayjs(a.start).unix() - dayjs(b.start).unix(),
      },
      {
        title: t('calendar:endTime'),
        dataIndex: 'end',
        key: 'end',
        render: (date: Date) => dayjs(date).format('MMM D, YYYY h:mm A'),
        sorter: (a: CalendarEvent, b: CalendarEvent) => dayjs(a.end).unix() - dayjs(b.end).unix(),
      },
      {
        title: t('calendar:organizer'),
        key: 'organizer',
        render: (record: CalendarEvent) => {
          if (record.hostedBy) {
            return (
              <Button
                type="link"
                size="small"
                onClick={(e) => handleOrganizerClick(record.hostedBy!.id, record.hostedBy!.type, e)}
              >
                {record.hostedBy.name} ({record.hostedBy.type})
              </Button>
            );
          }
          return '-';
        },
      },
      {
        title: t('common:comments'),
        dataIndex: 'comments',
        key: 'comments',
        render: (comments: any[] = []) => (
          <Badge count={comments.length} size="small">
            <MessageOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
          </Badge>
        ),
      },
      {
        title: t('common:actions.title'),
        key: 'actions',
        render: (record: CalendarEvent) => (
          <Space>
            <Tooltip title={t('common:actions.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditEvent(record)}
              />
            </Tooltip>
            <Tooltip title={t('common:actions.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteEvent(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <>
        {screens.xs ? (
          <List
            dataSource={eventsList}
            renderItem={renderEventCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
          />
        ) : (
          <Table
            dataSource={eventsList}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`,
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </>
    );
  }, [eventsList, handleViewEvent, handleEditEvent, handleDeleteEvent, handleOrganizerClick, renderEventCard, screens.xs, t]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderView = () => {
    switch (viewMode) {
      case 'calendar':
        return renderCalendarView();
      case 'table':
        return renderTableView();
      default:
        return renderCalendarView();
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>{t('calendar:title')}</Title>
        </Col>
        <Col>
          <Space>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <RadioButton value="calendar">
                <CalendarOutlined /> {t('calendar:calendarView')}
              </RadioButton>
              <RadioButton value="table">
                <TableOutlined /> {t('calendar:tableView')}
              </RadioButton>
            </Radio.Group>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEvent}
            >
              {t('calendar:addEvent')}
            </Button>
          </Space>
        </Col>
      </Row>

      {renderView()}

      {/* Calendar Event Modal */}
      <CalendarEventModal
        open={isModalOpen}
        event={selectedEvent}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('calendar:deleteConfirm')}
        error={deleteError}
      />
    </div>
  );
} 